// Compatibility layer for the original Adhkar-pack UI. The browser and editor
// keep their established shapes and styling while persistence is owned by the
// Spring Boot API instead of Firebase Realtime Database.
import { adhkarPacksApi } from '../api/adhkarPacks';
import { progressApi } from '../api/progress';
import { defaultAdhkar, readOfflineAdhkar, saveOfflineAdhkar } from '../services/offlineAdhkar';

export const MIN_ADHKAR_COUNT = 20;
export const OFFICIAL_PACK_ID = 'official';
const CHANGED_EVENT = 'amposter:adhkar-packs-changed';
const changed = () => window.dispatchEvent(new Event(CHANGED_EVENT));
const selectionVersions = new Map();
const selectionKey = (userId, type) => `${userId}:${type}`;

export async function fetchActivePackId(userId, type) {
  try { return (await progressApi.adhkarPreferences(type))?.activePackId || OFFICIAL_PACK_ID; }
  catch (_) { return readOfflineAdhkar(userId, type).packId; }
}

export async function setActivePackId(userId, type, packId) {
  const key = selectionKey(userId, type);
  selectionVersions.set(key, (selectionVersions.get(key) || 0) + 1);
  const pack = packId === OFFICIAL_PACK_ID ? null : await fetchPack(packId);
  const items = pack ? pack.items.filter(item => (item.itemType || 'both') === 'both' || item.itemType === type) : defaultAdhkar(type);
  await adhkarPacksApi.activate(type, packId);
  selectionVersions.set(key, (selectionVersions.get(key) || 0) + 1);
  saveOfflineAdhkar(userId, type, packId, items, pack?.meta?.title || pack?.name || '');
  changed();
}

export async function fetchPack(packId) { return adhkarPacksApi.get(packId); }
export async function fetchPackItems(packId) { return (await fetchPack(packId))?.items || []; }

export async function getActivePackItems(userId, type, officialListFallback) {
  const key = selectionKey(userId, type), version = selectionVersions.get(key) || 0;
  const save = (id, items, name) => {
    if ((selectionVersions.get(key) || 0) === version) {
      try { saveOfflineAdhkar(userId, type, id, items, name); } catch { /* Reading still works when device storage is full. */ }
    }
  };
  const packId = await fetchActivePackId(userId, type);
  if (packId === OFFICIAL_PACK_ID) {
    save(packId, officialListFallback);
    return officialListFallback;
  }
  try {
    const pack = await fetchPack(packId), items = pack?.items || [];
    const filtered = items.filter((item) => (item.itemType || 'both') === 'both' || item.itemType === type);
    save(packId, filtered, pack?.meta?.title || pack?.name || '');
    return filtered;
  } catch (_) {
    const saved = readOfflineAdhkar(userId, type);
    return saved.packId === packId ? saved.items : officialListFallback;
  }
}

export async function banUserFromPacks(userId) { await adhkarPacksApi.setCreationBan(userId, true); changed(); }
export async function unbanUserFromPacks(userId) { await adhkarPacksApi.setCreationBan(userId, false); changed(); }

export async function createPack(_userId, _userName, details) {
  const result = await adhkarPacksApi.create(details); changed(); return result;
}
export function isPackEditor(pack, userId) {
  const authorId = pack?.authorId ?? pack?.meta?.authorId;
  return Boolean(userId && pack && (authorId === userId || pack.editors?.[userId]));
}
export function isPackAuthor(pack, userId) { return Boolean(userId && (pack?.authorId ?? pack?.meta?.authorId) === userId); }
export async function fetchPackOwnership(packId) {
  const pack = await fetchPack(packId);
  return { authorId: pack.meta.authorId, editors: pack.editors || {} };
}
export async function updatePackDraft(packId, items) { const result = await adhkarPacksApi.updateItems(packId, items); changed(); return result; }
export async function updatePackMeta(packId, patch) { const result = await adhkarPacksApi.updateMeta(packId, patch); changed(); return result; }
export async function publishPack(packId, _userId, changelog = { ar: '', en: '' }) { const result = await adhkarPacksApi.publish(packId, changelog); changed(); return result; }
export async function unpublishPack(packId) { const result = await adhkarPacksApi.unpublish(packId); changed(); return result; }

export async function installPack(_userId, packId) { await adhkarPacksApi.install(packId); changed(); }
export async function fetchInstalledPackIds() { return (await adhkarPacksApi.installed()).map((entry) => entry.packId); }
export async function uninstallPack(userId, packId) {
  await adhkarPacksApi.uninstall(packId);
  for (const kind of ['morning', 'evening']) {
    const key = selectionKey(userId, kind);
    selectionVersions.set(key, (selectionVersions.get(key) || 0) + 1);
    if (readOfflineAdhkar(userId, kind).packId === packId) saveOfflineAdhkar(userId, kind, OFFICIAL_PACK_ID, defaultAdhkar(kind));
  }
  changed();
}
export async function checkForPackUpdates() { return []; }
export async function updateInstalledPack() { return null; }

export async function ratePack(_userId, packId, stars, text = '') { await adhkarPacksApi.rate(packId, stars, text); changed(); }
export async function fetchUserPackRating(_userId, packId) { return adhkarPacksApi.myRating(packId); }
export async function fetchPackRatings(packId) { return adhkarPacksApi.ratings(packId); }

export async function togglePackBookmark(_userId, packId, bookmarked) { await adhkarPacksApi.bookmark(packId, bookmarked); changed(); }
export async function fetchBookmarkedPackIds() { return [...(await adhkarPacksApi.bookmarks())]; }

export async function moderatorVerifyPack(packId) { const result = await adhkarPacksApi.moderate(packId, 'VERIFY'); changed(); return result; }
export async function moderatorUnverifyPack(packId) { const result = await adhkarPacksApi.moderate(packId, 'UNVERIFY'); changed(); return result; }
export async function moderatorRejectPack(packId) { const result = await adhkarPacksApi.moderate(packId, 'REJECT'); changed(); return result; }
export async function moderatorRemovePack(packId, _moderatorId, note = '') { const result = await adhkarPacksApi.moderate(packId, 'REMOVE', note); changed(); return result; }

export async function fetchUserPacks() { return adhkarPacksApi.mine(); }
export async function searchPacks(type, filters = {}, sort = 'popularity') {
  return adhkarPacksApi.browse({ type, query: filters.query, verifiedOnly: filters.verifiedOnly, sort });
}

export function subscribeToPacks(type, filters = {}, sort = 'popularity', callback) {
  let cancelled = false;
  const refresh = async () => {
    try { const packs = await searchPacks(type, filters, sort); if (!cancelled) callback(packs); }
    catch (error) { if (!cancelled) { console.error('adhkar-packs: browse failed', error); callback([]); } }
  };
  const onChange = () => refresh();
  refresh();
  window.addEventListener(CHANGED_EVENT, onChange);
  const timer = window.setInterval(refresh, 30000);
  return () => { cancelled = true; window.clearInterval(timer); window.removeEventListener(CHANGED_EVENT, onChange); };
}

// Retained as harmless compatibility hooks for old callers and browser consoles.
export async function syncPackIndexNow() { return null; }
export async function backfillPackIndex() { return 0; }
export function invalidatePackCache() {}
